/**
 * What an agent still has to do after requesting carriers on Altura, grouped
 * by where each carrier's contracting goes (see carrier-routing.ts). Shown on
 * the portal contracting page and mirrored in the confirmation email.
 *
 * Insurance Bay (SureLC) carriers have no card here: the portal's Insurance
 * Bay button is that next step. Paul retired the PPG SuranceBay link, the SMS
 * recruiting links, and the Pinnacle Spot form on Oct 31.
 */
import { CARRIER_DISPLAY_NAMES, CARRIER_SLUG_TO_CARRIER } from "./carriers.js";
import { DEFAULT_CARRIER_ROUTES, routeForCarrier, } from "./carrier-routing.js";
export const CONTRACTING_GUIDE_CONTACT_EMAIL = "contracting@alturalifehealth.com";
/**
 * Carriers that go through the Insurance Bay by default. Pages and jobs that
 * know the saved routes should use `insuranceBayCarriersFor` instead.
 */
export const INSURANCE_BAY_CARRIERS = Object.keys(DEFAULT_CARRIER_ROUTES).filter((carrier) => DEFAULT_CARRIER_ROUTES[carrier] === "SURELC");
const SECTIONS = {
    reagan: {
        id: "reagan",
        title: "Reagan.AI",
        summary: "We send the invite from our Reagan.AI account. Wait for the email from onboarding@reaganai.com before creating anything.",
        steps: [
            "Open the Reagan.AI email and choose “I am a new user,” then create your account.",
            "Answer the four questions and sign with your first and last name.",
            "When SuranceBay opens, complete every available contract in that one session.",
            "If carriers arrive in pieces, go to Requests after the account exists and add only the missing ones.",
        ],
        notes: [
            "Reagan Onboarding Assistance: 833-274-1953.",
            "Agent name on the bank account must match the contracting party if Reagan asks you to update EFT details.",
        ],
        contact: CONTRACTING_GUIDE_CONTACT_EMAIL,
    },
    direct_invite: {
        id: "direct_invite",
        title: "Email straight from the carrier",
        summary: "We invite you from our account with these carriers. Their email comes to the address you signed up with.",
        steps: [
            "Watch your inbox, and your spam folder, for the carrier’s contracting email.",
            "Open it and finish the carrier’s own steps. Use the same name and email as your Altura account.",
        ],
        contact: CONTRACTING_GUIDE_CONTACT_EMAIL,
    },
    manual: {
        id: "manual",
        title: "Altura contracting team",
        summary: "Our contracting team starts these by hand and will email you the next step.",
        steps: ["Nothing to do yet. Reply to that email when it arrives."],
        contact: CONTRACTING_GUIDE_CONTACT_EMAIL,
    },
};
const SECTION_ORDER = ["reagan", "direct_invite", "manual"];
const SECTION_FOR_ROUTE = {
    SURELC: null,
    REAGAN: "reagan",
    DIRECT_INVITE: "direct_invite",
    MANUAL: "manual",
};
/** Contract statuses that still need the agent to follow through. */
const FOLLOW_THROUGH_STATUSES = new Set(["PENDING", "ACTIVE"]);
export function resolveCarrierFromContractValue(value) {
    const trimmed = value.trim();
    if (!trimmed)
        return null;
    return (CARRIER_SLUG_TO_CARRIER[trimmed.toLowerCase()] ??
        (trimmed in CARRIER_DISPLAY_NAMES ? trimmed : null));
}
export function contractNeedsOnboardingFollowThrough(status) {
    return FOLLOW_THROUGH_STATUSES.has(status.trim().toUpperCase());
}
export function resolveContractingGuide(contracts, routes = {}) {
    const matching = new Map();
    for (const contract of contracts) {
        if (!contractNeedsOnboardingFollowThrough(contract.status))
            continue;
        const carrier = resolveCarrierFromContractValue(contract.carrier);
        if (!carrier)
            continue;
        const id = SECTION_FOR_ROUTE[routeForCarrier(carrier, routes)];
        if (!id)
            continue;
        const list = matching.get(id) ?? [];
        if (!list.includes(carrier))
            list.push(carrier);
        matching.set(id, list);
    }
    return SECTION_ORDER.flatMap((id) => {
        const carriers = matching.get(id);
        if (!carriers?.length)
            return [];
        return [
            {
                ...SECTIONS[id],
                matchingCarriers: carriers,
                matchingLabels: carriers.map((carrier) => CARRIER_DISPLAY_NAMES[carrier]),
            },
        ];
    });
}
/** Pending carriers the agent still has to request in their Insurance Bay. */
export function insuranceBayCarriersFor(contracts, routes = {}) {
    const found = [];
    for (const contract of contracts) {
        if (contract.status.trim().toUpperCase() !== "PENDING")
            continue;
        const carrier = resolveCarrierFromContractValue(contract.carrier);
        if (carrier && routeForCarrier(carrier, routes) === "SURELC" && !found.includes(carrier)) {
            found.push(carrier);
        }
    }
    return found;
}
